import { createFileRoute } from "@tanstack/react-router";
import { PageDatesBadge } from "@/components/site/PageDatesBadge";
import { SiteShell, PageHero } from "@/components/site/SiteShell";
import { Section } from "@/components/site/Blocks";
import { LicensingDisclosure } from "@/components/site/LicensingDisclosure";
import { SITE } from "@/data/site";
import { buildHead } from "@/lib/seo-head";
import { PageEssentials, PageOutro } from "@/components/site/PageEssentials";

const URL = "/legal/accessibility";

export const Route = createFileRoute("/legal/accessibility")({
  head: () =>
    buildHead({
      title: "Accessibility Statement | Simply Approved Mortgages",
      description:
        "Our commitment to making dscrloanmarket.com usable by everyone, the standard we work toward, and how to request assistance or report a barrier.",
      url: URL,
      keywords:
        "accessibility statement, WCAG 2.1 AA, website accessibility, ADA accessibility mortgage website, accessible loan application, screen reader support, request accommodation",
      breadcrumbs: [
        { name: "Home", url: "/" },
        { name: "Legal", url: "/legal" },
        { name: "Accessibility", url: URL },
      ],
      localBusiness: false,
    }),
  component: () => (
    <SiteShell>
      <PageHero eyebrow="Legal" title="Accessibility Statement" />
      <PageEssentials />
      <Section>
        <div className="prose prose-slate mx-auto max-w-3xl">
          <PageDatesBadge className="not-prose mb-4" />

          <p>
            {SITE.brand} is committed to making this website usable by as many people as possible,
            including investors who use screen readers, keyboard-only navigation, magnification, or
            other assistive technology.
          </p>

          <h2>Standard we work toward</h2>
          <p>
            We aim to conform substantially with the Web Content Accessibility Guidelines (WCAG) 2.1
            Level AA. That includes text alternatives for meaningful images, keyboard operability,
            visible focus states, sufficient color contrast, labeled form fields and calculator
            inputs, and content structured with proper headings and landmarks.
          </p>

          <h2>Ongoing effort</h2>
          <p>
            Accessibility is an ongoing process rather than a one-time project. We review new pages,
            calculators, and pricing tools as they are released, and we correct issues we find or
            that are reported to us. Some third-party content we embed — such as mapping widgets,
            scheduling tools, or lender-hosted portals — is outside our direct control, and its
            accessibility may vary. We do not represent or warrant that every page or feature is
            free of accessibility defects at all times.
          </p>

          <h2>Need help, or found a barrier?</h2>
          <p>
            If any part of this site prevents you from getting the information you need, we will
            provide the same information and the same service through another channel — by phone,
            email, or in an alternative format — at no additional cost to you. Call {SITE.phone} or
            use our <a href="/contact">contact form</a> and describe the page and the problem. We
            respond to accessibility requests as promptly as we reasonably can.
          </p>

          <h2>Equal Housing Opportunity</h2>
          <p>
            {SITE.brand} does not discriminate on the basis of race, color, religion, national
            origin, sex, familial status, age (provided the applicant has the capacity to contract),
            disability, or because all or part of an applicant's income comes from a public
            assistance program.
          </p>

          <LicensingDisclosure className="not-prose mt-6 text-sm text-muted-foreground" />
        </div>
      </Section>
    <PageOutro />
      </SiteShell>
  ),
});
